import { getMessaging, getToken, isSupported, onMessage, type MessagePayload } from 'firebase/messaging'
import { getAuth } from 'firebase/auth'
import { getFirebaseApp } from '../../config/firebase'
import { firebaseConfig } from '../../config/firebase'
import { saveOwnNotificationToken, saveOwnNotificationTokenError } from '../../services/firestore/notificationTokenService'
import { getStableDeviceId } from './deviceIdentity'
import { adminTodayRemindersTarget, normalizeNotificationTarget } from './notificationDeepLink'
import { adminDailyOpenSummaryEndpoint } from '../../services/api/notificationEndpoint'

export type AdminCatchUpResult =
  | { ok: true; sent: boolean; remainingCount?: number }
  | { ok: false; retryable: true; status?: number; reason: string }

export type FcmRegistrationResult =
  | { ok: true; token: string; catchUp?: AdminCatchUpResult }
  | { ok: false; reason: string }

function getBrowserName(userAgent: string): string {
  if (userAgent.includes('Edg/')) return 'Edge'
  if (userAgent.includes('Chrome/')) return 'Chrome'
  if (userAgent.includes('Firefox/')) return 'Firefox'
  if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) return 'Safari'
  return 'Unknown browser'
}

function deviceMetadata() {
  const userAgent = navigator.userAgent || ''
  return { browser: getBrowserName(userAgent), platform: navigator.platform || '', userAgent }
}

function catchUpFailure(reason: string, status?: number): AdminCatchUpResult {
  return { ok: false, retryable: true, ...(status === undefined ? {} : { status }), reason }
}

export function catchUpResultFromResponse(response: {
  ok: boolean
  status: number
  body?: { sent?: unknown; remainingCount?: unknown; error?: unknown } | null
}): AdminCatchUpResult {
  if (!response.ok) {
    const reason = typeof response.body?.error === 'string' ? response.body.error : `Catch-up request failed with HTTP ${response.status}.`
    return catchUpFailure(reason, response.status)
  }
  return { ok: true, sent: response.body?.sent === true, ...(typeof response.body?.remainingCount === 'number' ? { remainingCount: response.body.remainingCount } : {}) }
}

export async function requestAdminDailyOpenSummary(deviceId: string): Promise<AdminCatchUpResult> {
  try {
    const auth = getAuth(getFirebaseApp())
    const currentUser = auth.currentUser
    if (!currentUser) return catchUpFailure('Signed-in admin session is unavailable for catch-up.')
    const idToken = await currentUser.getIdToken()
    const endpoint = adminDailyOpenSummaryEndpoint(
      import.meta.env.VITE_NOTIFICATION_WORKER_BASE_URL,
      import.meta.env.VITE_FIREBASE_FUNCTIONS_BASE_URL,
      firebaseConfig.projectId,
      import.meta.env.PROD,
    )
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceId }),
    })
    const body = await response.json().catch(() => null)
    const result = catchUpResultFromResponse({ ok: response.ok, status: response.status, body })
    if (!result.ok) console.warn('[notifications] Admin catch-up failed; FCM registration remains successful and retry is safe.', result)
    return result
  } catch (error) {
    const reason = error instanceof Error && error.message ? error.message : 'Admin catch-up request could not be completed.'
    const result = catchUpFailure(reason)
    console.warn('[notifications] Admin catch-up could not be completed; FCM registration remains successful and retry is safe.', result)
    return result
  }
}

async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!('serviceWorker' in navigator)) return undefined
  const registration = await Promise.race<ServiceWorkerRegistration | undefined>([
    navigator.serviceWorker.ready,
    new Promise<undefined>((resolve) => window.setTimeout(() => resolve(undefined), 8000)),
  ])
  if (!registration) throw new Error('The notification service worker is not ready. Please refresh and try again.')
  registration.active?.postMessage({ type: 'SGS_FIREBASE_CONFIG', firebaseConfig })
  return registration
}

export async function registerFcmDeviceToken(input: { uid: string; schoolId: string; role: string }): Promise<FcmRegistrationResult> {
  const metadata = deviceMetadata()
  const deviceId = getStableDeviceId()
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY
  if (!vapidKey) {
    const reason = 'FCM VAPID public key is not configured.'
    await saveOwnNotificationTokenError({ ...input, ...metadata, deviceId, onboardingStatus: 'unknown', errorMessage: reason })
    return { ok: false, reason }
  }
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    const reason = 'Browser notification permission is not granted.'
    await saveOwnNotificationTokenError({ ...input, ...metadata, deviceId, onboardingStatus: 'unknown', errorMessage: reason })
    return { ok: false, reason }
  }
  if (!(await isSupported())) {
    const reason = 'Firebase Cloud Messaging is not supported on this browser/device.'
    await saveOwnNotificationTokenError({ ...input, ...metadata, deviceId, onboardingStatus: 'unsupported', errorMessage: reason })
    return { ok: false, reason }
  }
  try {
    const messaging = getMessaging(getFirebaseApp())
    const serviceWorkerRegistration = await getServiceWorkerRegistration()
    const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration })
    if (!token) {
      const reason = 'Firebase did not return a device token.'
      await saveOwnNotificationTokenError({ ...input, ...metadata, deviceId, onboardingStatus: 'unknown', errorMessage: reason })
      return { ok: false, reason }
    }
    await saveOwnNotificationToken({ ...input, ...metadata, deviceId, onboardingStatus: 'enabled', token, status: 'active' })
    let catchUp: AdminCatchUpResult | undefined
    if (input.role === 'admin') catchUp = await requestAdminDailyOpenSummary(deviceId)
    return { ok: true, token, ...(catchUp ? { catchUp } : {}) }
  } catch (error) {
    const reason = error instanceof Error && error.message ? error.message : 'FCM token registration failed.'
    await saveOwnNotificationTokenError({ ...input, ...metadata, deviceId, onboardingStatus: 'unknown', errorMessage: reason })
    return { ok: false, reason }
  }
}

export async function subscribeToForegroundFcmMessages(onPayload: (payload: MessagePayload) => void): Promise<() => void> {
  if (!(await isSupported())) return () => undefined
  return onMessage(getMessaging(getFirebaseApp()), onPayload)
}

export function attachForegroundNotificationClick(notification: Notification, target = adminTodayRemindersTarget): void {
  const safeTarget = normalizeNotificationTarget(target, window.location.origin)
  if (!safeTarget) return
  notification.onclick = () => { window.focus(); window.location.assign(safeTarget) }
}